"use client";

import { MapPin } from "lucide-react";
import { useTranslations } from "next-intl";
import { Control, useFormContext } from "react-hook-form";

import { BankValuationCompleteFormSchemaType } from "@/schemas/validation/bankValuationSchema";
import LocationPicker from "./LocationPicker";

interface BankValuationSimpleLocationStepProps {
  control: Control<any>;
  hasError?: boolean;
}

const BankValuationSimpleLocationStep = (_props: BankValuationSimpleLocationStepProps) => {
  const tLocation = useTranslations("BankValuation.simpleLocationStep");
  const tInspection = useTranslations("BankValuation.inspectionStep");

  const { setValue, watch } = useFormContext<BankValuationCompleteFormSchemaType>();

  const locationAddress = watch("inspectionLocation" as any) as string | undefined;
  const hasLocation = !!locationAddress?.trim();

  const handleLocationChange = (address: string, lat: number, lng: number) => {
    setValue("inspectionLocation" as any, address, { shouldDirty: true });
    setValue("inspectionLatitude" as any, lat, { shouldDirty: true });
    setValue("inspectionLongitude" as any, lng, { shouldDirty: true });
  };

  return (
    <div className="mt-6 space-y-6">
      {/* Info banner */}
      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
        <div className="flex items-start gap-3">
          <MapPin size={18} className="mt-0.5 shrink-0 text-primary" />
          <div>
            <h4 className="font-urbanist text-sm font-bold text-primary-foreground">{tLocation("infoTitle")}</h4>
            <p className="inter mt-1 text-sm text-secondary-foreground">{tLocation("infoDescription")}</p>
          </div>
        </div>
      </div>

      {/* Location picker */}
      <section>
        <h3 className="mb-3 flex items-center gap-2 font-urbanist text-base font-bold text-primary-foreground">
          <MapPin size={17} />
          {tLocation("locationTitle")}
          <span className="text-red-400">*</span>
        </h3>
        <LocationPicker onLocationChange={handleLocationChange} t={(key) => tInspection(key as any)} />

        {!hasLocation && locationAddress !== undefined && (
          <p className="inter mt-2 text-sm text-red-500">{tLocation("locationRequired")}</p>
        )}
      </section>

      {/* Confirmation row */}
      {hasLocation && (
        <div className="flex items-start gap-2 rounded-xl border border-green-100 bg-green-50 px-4 py-3">
          <MapPin size={15} className="mt-0.5 shrink-0 text-green-600" />
          <p className="inter text-sm font-medium text-green-800">✓ {locationAddress}</p>
        </div>
      )}
    </div>
  );
};

export default BankValuationSimpleLocationStep;
